import { ActivationAttempt, GameState, Player, PreparedCard, CardType, Card, CardColor, EffectResult, GamePhase } from '../types'
import { getCardEffect, isImmuneToDamage } from './gameLogic'
import { applyHealthLoss } from './effects'
import { shuffle } from './decks'
import { xmur3, mulberry32 } from './random'
import { getEffectiveHero } from './heroes'
import { generateTowerId } from './towers'

// Logging disabled to improve FPS
const APPLY_EFFECT_DEBUG = false

/**
 * PHASE 4A: Apply card effects with tree-aware priority system (Option A: Minimal Integration)
 * 
 * This function applies the effect of a winning card from an activation.
 * 
 * CURRENT APPROACH (Option A):
 * - Integrating getAffectedChains() into existing card logic
 * - Tree-aware checks for damage blocking and modification
 * - Incremental refactoring by card type
 * 
 * FUTURE EVOLUTION (Option B):
 * - Build centralized EffectResult system
 * - Priority-based effect resolution with cancellation
 * - Universal effect interaction framework
 * 
 * @param winningCard - The card that won the counterChain resolution
 * @param activation - The activation this card belongs to
 * @param state - Current game state
 * @param effectResult - Optional tree-aware effect data with calculated values
 * @returns Updated game state after applying the effect
 */
export const applyCardEffect = (
    winningCard: PreparedCard,
    activation: ActivationAttempt,
    state: GameState,
    effectResult?: EffectResult  // NEW: optional tree-aware effect data
): GameState => {
    const log = (tag: string, message: string) => {
        if (APPLY_EFFECT_DEBUG) console.log(`[${tag}] ${message}`)
    }

    log('applyCardEffect', `🎯 Applying effect for card: ${getCardEffect(winningCard).type}`)

    // Deep copy to avoid mutations
    let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players))
    let newColorDecks: Record<CardColor, Card[]> = JSON.parse(JSON.stringify(state.colorDecks))
    let newStashDeck = [...state.stashDeck]
    let newStashDiscard = [...state.stashDiscard]
    let newDealingDeck = [...state.dealingDeck]
    let newAvailableTowers = [...state.availableTowers]
    let newDrawEvents = [...state.drawEvents]
    let newEliminatedCards = [...state.eliminatedCards]
    const returnedToColors = new Set<CardColor>()
    let newLog = [...state.log]
    let towerVictor: Player | null = null
    let finalTargetId: number | undefined = activation.targetId
    let chromabowChoicePendingForPlayerId: number | null = null
    let hibernationChoicePendingForPlayerId: number | null = null
    let burglaryChoicePendingForPlayerId: number | null = null
    let burglaryTargetId: number | null = null
    let purificationChoicePendingForPlayerId: number | null = null
    let extraDayToSchedule = 0

    const damageMap: Record<string, number> = { ...(state.damageCardsUsedThisNight || {}) }
    const attemptedPre: Record<string, number> = { ...(state.attemptedDamageCardsThisNight || {}) }
    const attemptedMap: Record<string, number> = { ...attemptedPre }

    const incDamageUse = (attId: number, defId: number) => {
        const key = `${attId}->${defId}`
        damageMap[key] = (damageMap[key] || 0) + 1
    }
    const incAttempt = (attId: number, defId: number) => {
        const key = `${attId}->${defId}`
        attemptedMap[key] = (attemptedMap[key] || 0) + 1
    }

    const attacker = playersCopy.find(p => p.id === winningCard.card.ownerId)!
    if (!finalTargetId) {
        finalTargetId = activation.targetId ?? (attacker.id === 1 ? 2 : 1)
    }
    const defender = playersCopy.find(p => p.id === finalTargetId)!
    const { type: winningCardType, value: winningCardValue } = getCardEffect(winningCard)

    // PHASE 1a: Apply card effect based on type
    switch (winningCardType) {
        case CardType.Attack: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalAttackValue = winningCardValue
                if (damageReduction > 0) {
                    finalAttackValue = Math.max(0, winningCardValue - damageReduction)
                }
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalAttackValue += 1
                const damage = Math.max(0, finalAttackValue - defender.shield)
                const damageLogs = applyHealthLoss(defender, damage, attacker)
                defender.shield = Math.max(0, defender.shield - finalAttackValue)
                newLog.push(...damageLogs)
            }
            break
        }

        case CardType.InfernoGauntlet: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalAttackValue = winningCardValue
                if (damageReduction > 0) {
                    finalAttackValue = Math.max(0, winningCardValue - damageReduction)
                }
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalAttackValue += 1
                const damageDealt = Math.max(0, finalAttackValue - defender.shield)
                const damageLogs = applyHealthLoss(defender, damageDealt, attacker)
                defender.shield = Math.max(0, defender.shield - finalAttackValue)
                newLog.push(...damageLogs)
                if (damageDealt > 0 && defender.hand.length > 0) {
                    const cardsToDiscard = Math.min(2, defender.hand.length)
                    const discardedCards: Card[] = []
                    const discardSeed = state.seed + `_round${state.round}_inferno_${winningCard.card.id}`
                    const seedGen = xmur3(discardSeed)
                    const rand = mulberry32(seedGen())
                    for (let i = 0; i < cardsToDiscard; i++) {
                        if (defender.hand.length > 0) {
                            const randomIndex = Math.floor(rand() * defender.hand.length)
                            const discardedCard = defender.hand.splice(randomIndex, 1)[0]
                            discardedCards.push(discardedCard)
                            if (discardedCard.essenceCost) {
                                newStashDiscard.push(discardedCard)
                            } else {
                                newColorDecks[discardedCard.color].push(discardedCard)
                                returnedToColors.add(discardedCard.color)
                            }
                        }
                    }
                }
            }
            break
        }

        case CardType.Larcendagger: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalAttackValue = winningCardValue
                if (damageReduction > 0) {
                    finalAttackValue = Math.max(0, winningCardValue - damageReduction)
                }
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalAttackValue += 1
                const damageDealt = Math.max(0, finalAttackValue - defender.shield)
                const damageLogs = applyHealthLoss(defender, damageDealt, attacker)
                defender.shield = Math.max(0, defender.shield - finalAttackValue)
                newLog.push(...damageLogs)
                if (damageDealt > 0 && defender.hand.length > 0) {
                    const cardsToSteal = Math.min(2, defender.hand.length)
                    const stolenCards: Card[] = []
                    const stealSeed = state.seed + `_round${state.round}_larcendagger_${winningCard.card.id}`
                    const seedGen = xmur3(stealSeed)
                    const rand = mulberry32(seedGen())
                    for (let i = 0; i < cardsToSteal; i++) {
                        if (defender.hand.length > 0) {
                            const randomIndex = Math.floor(rand() * defender.hand.length)
                            const stolenCard = defender.hand.splice(randomIndex, 1)[0]
                            stolenCard.ownerId = attacker.id
                            attacker.hand.push(stolenCard)
                            stolenCards.push(stolenCard)
                        }
                    }
                }
            }
            break
        }

        case CardType.LifeDrain: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalDrainDamage = winningCardValue
                if (damageReduction > 0) {
                    finalDrainDamage = Math.max(0, winningCardValue - damageReduction)
                }
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalDrainDamage += 1
                const damageDealt = Math.max(0, finalDrainDamage - defender.shield)
                const damageLogs = applyHealthLoss(defender, damageDealt, attacker)
                defender.shield = Math.max(0, defender.shield - finalDrainDamage)
                newLog.push(...damageLogs)
                const healAmount = Math.min(winningCardValue, attacker.maxHealth - attacker.health)
                attacker.health = Math.min(attacker.maxHealth, attacker.health + winningCardValue)
            }
            break
        }

        case CardType.EnergySword: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalAttackValue = winningCardValue
                if (damageReduction > 0) {
                    finalAttackValue = Math.max(0, winningCardValue - damageReduction)
                }
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalAttackValue += 1
                const damage = Math.max(0, finalAttackValue - defender.shield)
                const damageLogs = applyHealthLoss(defender, damage, attacker)
                defender.shield = Math.max(0, defender.shield - finalAttackValue)
                newLog.push(...damageLogs)
            }
            const energizeValue = 2
            attacker.disruptedSlots -= energizeValue
            break
        }

        case CardType.FlyingScythe: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (defenderIsImmune || effectResult?.damagePrevented) break
            const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
            const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
            let finalBaseAttackValue = winningCardValue
            if (damageReduction > 0) {
                finalBaseAttackValue = Math.max(0, winningCardValue - damageReduction)
            }
            const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
            if (voodooActive) finalBaseAttackValue += 1
            const baseDamageDealt = Math.max(0, finalBaseAttackValue - defender.shield)
            const damageLogs = applyHealthLoss(defender, baseDamageDealt, attacker)
            defender.shield = Math.max(0, defender.shield - finalBaseAttackValue)
            newLog.push(...damageLogs)
            const missingHealth = defender.maxHealth - defender.health
            if (missingHealth > 0) {
                const bonusDamage = Math.floor(missingHealth / 2)
                const bonusWithVoodoo = voodooActive ? bonusDamage + 1 : bonusDamage
                if (bonusWithVoodoo > 0) {
                    const bonusDamageDealt = Math.max(0, bonusWithVoodoo - defender.shield)
                    const bonusLogs = applyHealthLoss(defender, bonusDamageDealt, attacker)
                    defender.shield = Math.max(0, defender.shield - bonusWithVoodoo)
                    newLog.push(...bonusLogs)
                }
            }
            break
        }

        case CardType.Chromabow: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)

            // Only apply damage if NOT immune AND damage wasn't prevented by tree logic (e.g. Counterblast)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                const base = voodooActive ? winningCardValue + 1 : winningCardValue
                const damage = Math.max(0, base - defender.shield)
                const damageLogs = applyHealthLoss(defender, damage, attacker)
                defender.shield = Math.max(0, defender.shield - base)
                newLog.push(...damageLogs)
            } else if (effectResult?.damagePrevented) {
                log('applyCardEffect', 'Chromabow damage prevented (e.g. by Counterblast/Invincible), but Choice effect persists.')
            }

            // Choice logic runs regardless of damage prevention (unless defender is immune? No, user requested Choice persists)
            // Note: If defender is Invincible, isImmuneToDamage is true. But user requested Invincible should NOT block choice.
            // So we execute choice logic as long as card wasn't fully cancelled (which loop handles).

            const availableColorDecks = Object.values(CardColor).filter(color => newColorDecks[color]?.length > 0)
            if (availableColorDecks.length > 0) {
                // Neural agents go through choice phase; rule-based AI auto-selects
                if (attacker.isAI && !attacker.isNeuralAgent) {
                    const drawSeed = state.seed + `_chromabow_draw_${winningCard.card.id}`
                    const seedGen = xmur3(drawSeed)
                    const rand = mulberry32(seedGen())
                    const chosenColor = availableColorDecks[Math.floor(rand() * availableColorDecks.length)]
                    const cardToDraw = newColorDecks[chosenColor].pop()!
                    cardToDraw.ownerId = attacker.id
                    attacker.hand.push(cardToDraw)
                } else {
                    chromabowChoicePendingForPlayerId = attacker.id
                }
            }
            break
        }


        // PHASE 1b: Shield and Defense cards
        case CardType.Defense: {
            const isShimmeringShield = winningCard.card.subType === 'Armory' && (
                (winningCard.card as any).faceSlug === 'shimmering-shield' ||
                (winningCard.card.extendedShield && winningCard.card.value === 2 && winningCard.card.essenceCost === 5)
            )
            const isSpikes = winningCard.card.subType === 'Armory' && (
                (winningCard.card as any).faceSlug === 'spikes' ||
                (winningCard.card.extendedShield && winningCard.card.value === 0 && winningCard.card.essenceCost === 6)
            )
            if (isShimmeringShield) {
                const missing = attacker.maxHealth - attacker.health
                const bonus = Math.floor(Math.max(0, missing) / 2)
                const computedDefense = 2 + bonus
                attacker.shield += computedDefense
                newLog.push(`${attacker.name}'s Shimmering Shield grants ${computedDefense} defense.`)
            } else if (isSpikes) {
                // Spikes damage was calculated in effect_resolution.ts based on tree
                if (effectResult && effectResult.damage !== undefined) {
                    if (effectResult.damagePrevented) {
                        log('applyCardEffect', 'Spikes damage prevented by Counterblast/Immunity.')
                        break
                    }
                    const spikesDamage = effectResult.damage
                    const targetId = effectResult.targetId
                    const target = playersCopy.find(p => p.id === targetId)!

                    log('applyCardEffect', `🛡️ SPIKES applying ${spikesDamage} damage to Player ${targetId}`)

                    // Apply damage through shield first
                    const damage = Math.max(0, spikesDamage - target.shield)
                    const damageLogs = applyHealthLoss(target, damage, attacker)
                    target.shield = Math.max(0, target.shield - spikesDamage)

                    newLog.push(...damageLogs)
                    newLog.push(`${attacker.name}'s Spikes deals ${spikesDamage} damage to ${target.name}!`)
                } else {
                    log('applyCardEffect', '⚠️ SPIKES has no effectResult - this should not happen in tree-aware resolution')
                }
            } else {
                attacker.shield += winningCardValue
            }
            break
        }
        case CardType.Counterblast: {
            // Counterblast reflects incoming damage back to the attacker
            // effectResult.damage = total reflected damage, effectResult.targetId = who gets it
            if (effectResult && effectResult.damage && effectResult.damage > 0) {
                const reflectTarget = playersCopy.find(p => p.id === effectResult.targetId)!
                const reflectSource = playersCopy.find(p => p.id === winningCard.card.ownerId)!
                incDamageUse(reflectSource.id, reflectTarget.id)
                const targetIsImmune = isImmuneToDamage(reflectTarget, state.round)
                if (!targetIsImmune) {
                    const cursedIronCards = reflectTarget.effectCards.filter(c => c.type === CardType.CursedIron)
                    const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                    let finalReflectDamage = effectResult.damage
                    if (damageReduction > 0) {
                        finalReflectDamage = Math.max(0, finalReflectDamage - damageReduction)
                    }
                    const voodooActive = reflectSource.effectCards.some(c => c.type === CardType.VoodooDoll)
                    if (voodooActive) finalReflectDamage += 1
                    const actualDamage = Math.max(0, finalReflectDamage - reflectTarget.shield)
                    const damageLogs = applyHealthLoss(reflectTarget, actualDamage, reflectSource)
                    reflectTarget.shield = Math.max(0, reflectTarget.shield - finalReflectDamage)
                    newLog.push(...damageLogs)
                    newLog.push(`${reflectSource.name}'s Counterblast reflects ${effectResult.damage} damage to ${reflectTarget.name}!`)
                }
            }
            break
        }

        case CardType.PlantedBomb: {
            const bombOwner = playersCopy.find(p => p.id === winningCard.card.ownerId)!
            const bombTarget = playersCopy.find(p => p.id !== bombOwner.id)!
            incDamageUse(bombOwner.id, bombTarget.id)
            const bombTargetIsImmune = isImmuneToDamage(bombTarget, state.round)
            if (!bombTargetIsImmune) {
                const cursedIronCards = bombTarget.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalDamage = winningCardValue
                if (damageReduction > 0) {
                    finalDamage = Math.max(0, winningCardValue - damageReduction)
                }
                const voodooActive = bombOwner.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalDamage += 1
                const actualDamage = Math.max(0, finalDamage - bombTarget.shield)
                const damageLogs = applyHealthLoss(bombTarget, actualDamage, bombOwner)
                bombTarget.shield = Math.max(0, bombTarget.shield - winningCardValue)
                newLog.push(...damageLogs)
            }
            break
        }

        case CardType.Protector: {
            const protectorOwner = playersCopy.find(p => p.id === winningCard.card.ownerId)!
            const rotation = winningCard.rotation
            const normalizedRotation = (rotation % 360 + 360) % 360
            const mode = normalizedRotation > 90 ? 'ATTACK' : 'DEFENSE'
            const isInitialActivation = winningCard.card.id === activation.attackingCard.card.id

            if (!isInitialActivation) {
                protectorOwner.shield += winningCardValue
            } else {
                if (mode === 'ATTACK') {
                    const target = playersCopy.find(p => p.id === finalTargetId)!
                    incDamageUse(attacker.id, target.id)
                    const targetIsImmune = isImmuneToDamage(target, state.round)
                    if (!targetIsImmune && !effectResult?.damagePrevented) {
                        const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                        const base = voodooActive ? winningCardValue + 1 : winningCardValue
                        const damage = Math.max(0, base - target.shield)

                        const damageLogs = applyHealthLoss(target, damage, attacker)
                        target.shield = Math.max(0, target.shield - base)
                        newLog.push(...damageLogs)
                    }
                } else {
                    protectorOwner.shield += winningCardValue
                }
            }
            // Always heal the OWNER, never the target
            const healAmount = Math.min(1, protectorOwner.maxHealth - protectorOwner.health)
            if (healAmount > 0) {
                protectorOwner.health += 1
            }
            break
        }

        case CardType.Hibernation: {
            // Shanu Hero Logic
            const defenderForHibernation = playersCopy.find(p => p.id === finalTargetId)!
            const attackerEffectiveHero = getEffectiveHero(attacker, defenderForHibernation)
            if (attackerEffectiveHero === 'SHANU') {
                const currentRound = state.round
                const lastRound = attacker.shanuLastHibernationRound || -1
                let streak = attacker.shanuHibernationStreak || 0

                if (currentRound > lastRound) {
                    if (currentRound === lastRound + 1) {
                        streak++
                    } else {
                        streak = 1
                    }
                    attacker.shanuLastHibernationRound = currentRound
                    attacker.shanuHibernationStreak = streak
                }

                if (streak >= 2) {
                    attacker.health = attacker.maxHealth
                    newLog.push(`${attacker.name} (Shanu) restores full HP from consecutive Hibernation!`)
                    newLog.push(`[REWARD] HERO_POWER_USED:PLAYER=${attacker.id}:TYPE=SHANU_FULL_HEAL`)
                }
            }

            const availableDrawDecks = Object.values(CardColor).filter(color => newColorDecks[color]?.length > 0)
            if (availableDrawDecks.length > 0) {
                // Neural agents go through choice phase; rule-based AI auto-selects
                if (attacker.isAI && !attacker.isNeuralAgent) {
                    const drawSeed = state.seed + `_hibernation_draw_${winningCard.card.id}`
                    const seedGen = xmur3(drawSeed)
                    const rand = mulberry32(seedGen())
                    const chosenColor = availableDrawDecks[Math.floor(rand() * availableDrawDecks.length)]
                    const cardToDraw = newColorDecks[chosenColor].pop()!
                    cardToDraw.ownerId = attacker.id
                    attacker.hand.push(cardToDraw)
                } else {
                    hibernationChoicePendingForPlayerId = attacker.id
                }
            }
            // Add Hibernation to effectCards so it can be tracked/animated
            // Standard Hibernation targets SELF (attacker is the one activating it)
            const hibernationMarker: Card = { ...winningCard.card, type: CardType.Hibernation, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(hibernationMarker)
            break
        }

        // PHASE 1c: Utility & Special cards
        case CardType.LifeCharger: {
            const healAmount = attacker.maxHealth - attacker.health
            attacker.health = attacker.maxHealth
            break
        }

        case CardType.NaturalGrip: {
            // NaturalGrip effect goes ON the DEFENDER (target, who is being gripped)
            // attacker = card owner (who played it), defender = target
            const targetIsImmune = isImmuneToDamage(defender, state.round)
            if (!targetIsImmune) {
                const gripMarker: Card = { ...winningCard.card, type: CardType.NaturalGrip, appliedRound: state.round, ownerId: attacker.id }
                defender.effectCards.push(gripMarker)
                newLog.push(`${attacker.name}'s Natural Grip takes hold of ${defender.name}!`)
            }
            break
        }

        case CardType.Disease: {
            log('applyCardEffect', `🦠 DISEASE: attacker.id=${attacker.id}, defender.id=${defender.id}, activation.targetId=${activation.targetId}`)
            const diseaseMarker: Card = { ...winningCard.card, type: CardType.Disease, appliedRound: state.round, ownerId: attacker.id }
            defender.effectCards.push(diseaseMarker)
            log('applyCardEffect', `🦠 DISEASE pushed to defender (${defender.id}) effectCards. Count: ${defender.effectCards.length}`)
            newLog.push(`${attacker.name} infects ${defender.name} with Disease!`)
            break
        }

        case CardType.SealOfSilence: {
            // SealOfSilence effect goes ON the DEFENDER (target, who is being silenced)
            // attacker = card owner (who played it), defender = target
            const alreadySilenced = defender.effectCards.some(c => c.type === CardType.SealOfSilence && c.ownerId === attacker.id && c.appliedRound === state.round)

            if (!alreadySilenced) {
                const silenceMarker: Card = { ...winningCard.card, type: CardType.SealOfSilence, appliedRound: state.round, ownerId: attacker.id }
                defender.effectCards.push(silenceMarker)
                newLog.push(`${attacker.name} seals ${defender.name}'s voice. Pink and Spell cards are disabled this night.`)
            }
            break
        }

        case CardType.VoodooDoll: {
            const voodooMarker: Card = { ...winningCard.card, type: CardType.VoodooDoll, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(voodooMarker)
            newLog.push(`${attacker.name} empowers their strikes with Voodoo Doll.`)
            break
        }

        case CardType.TwilightShift: {
            newLog.push(`${attacker.name} bends time with Twilight Shift! The next night will be skipped, causing two days in a row.`)
            extraDayToSchedule += 1
            break
        }

        case CardType.Heal: {
            const healTarget = playersCopy.find(p => p.id === finalTargetId)!
            const opponent = playersCopy.find(p => p.id !== healTarget.id)!

            let healAmount = winningCardValue
            const attackerEffectiveHero = getEffectiveHero(attacker, opponent)
            if (attackerEffectiveHero === 'NYRIX') {
                healAmount *= 2
                newLog.push(`${attacker.name}'s Nyrix doubles the healing effect!`)
            }

            const diseasesToRemove = healTarget.effectCards.filter(c => c.type === CardType.Disease)
            const naturalGripsToRemove = healTarget.effectCards.filter(c => c.type === CardType.NaturalGrip)

            const opponentEffectiveHero = getEffectiveHero(opponent, healTarget)
            if (opponentEffectiveHero === 'NYRIX' && diseasesToRemove.length > 0) {
                const otherEffects = healTarget.effectCards.filter(c => c.type !== CardType.Disease && c.type !== CardType.NaturalGrip)
                const remainingDiseases = diseasesToRemove.slice(1)
                healTarget.effectCards = [...otherEffects, ...remainingDiseases]
                newLog.push(`${healTarget.name} cleanses 1 Disease stack (Nyrix's curse lingers).`)
            } else {
                if (diseasesToRemove.length > 0 || naturalGripsToRemove.length > 0) {
                    healTarget.effectCards = healTarget.effectCards.filter(c => c.type !== CardType.Disease && c.type !== CardType.NaturalGrip)
                }
            }

            healTarget.health = Math.min(healTarget.maxHealth, healTarget.health + healAmount)
            break
        }

        case CardType.Steal: {
            if (winningCard.card.subType === 'Spell') {
                if (defender.hand.length > 0) {
                    burglaryChoicePendingForPlayerId = attacker.id
                    burglaryTargetId = defender.id
                }
            } else {
                if (defender.hand.length > 0) {
                    const stealSeed = state.seed + `_round${state.round}_turn${state.turnPlayerId}_steal_${activation.attackingCard.card.id}`
                    const seedGen = xmur3(stealSeed)
                    const rand = mulberry32(seedGen())
                    const stolenCardIndex = Math.floor(rand() * defender.hand.length)
                    const [stolenCard] = defender.hand.splice(stolenCardIndex, 1)
                    stolenCard.ownerId = attacker.id
                    attacker.hand.push(stolenCard)
                }
            }
            break
        }

        case CardType.StealTower: {
            // Check if already captured a tower this night
            if (attacker.capturedTowerThisTurn) {
                newLog.push(`${attacker.name}'s Purification fizzles - already captured a tower this night.`)
                break
            }

            // Neural agents go through choice phase; rule-based AI auto-selects
            if (attacker.isAI && !attacker.isNeuralAgent) {
                let stolenTowerColor: CardColor | undefined
                const stealSeed = state.seed + `_steal_tower_${winningCard.card.id}`
                const seedGen = xmur3(stealSeed)
                const rand = mulberry32(seedGen())
                if (defender.towers.length > 0) {
                    const stolenIndex = Math.floor(rand() * defender.towers.length)
                    const stolenTower = defender.towers[stolenIndex]
                    stolenTowerColor = stolenTower.color
                    defender.towers.splice(stolenIndex, 1)
                    attacker.towers.push(stolenTower)
                    newLog.push(`t:log.towerStole|playerName=${attacker.name}|targetColor=${stolenTowerColor}|defenderName=${defender.name}|method=purification`)
                    // Clear towerChangeMenu if it was open for the defender, as indices shifted
                    if (state.towerChangeMenu && state.towerChangeMenu.playerId === defender.id) {
                        state.towerChangeMenu = undefined
                    }
                } else if (newAvailableTowers.length > 0) {
                    const stolenIndex = Math.floor(rand() * newAvailableTowers.length)
                    stolenTowerColor = newAvailableTowers[stolenIndex]
                    newAvailableTowers.splice(stolenIndex, 1)
                    const allTowers = [...playersCopy[0].towers, ...playersCopy[1].towers]
                    attacker.towers.push({ color: stolenTowerColor, id: generateTowerId(stolenTowerColor, allTowers) })
                    newLog.push(`t:log.towerClaimed|playerName=${attacker.name}|targetColor=${stolenTowerColor}|method=purification`)
                } else {
                    newLog.push(`t:log.towerNoTarget|playerName=${attacker.name}`)
                }
                // Set flag after successful capture
                if (stolenTowerColor) {
                    attacker.capturedTowerThisTurn = true
                }
                if (stolenTowerColor && attacker.towers.length >= 5) towerVictor = attacker
            } else {
                const availableTargets = [...defender.towers, ...newAvailableTowers.map(color => ({ color }))]
                if (availableTargets.length > 0) {
                    purificationChoicePendingForPlayerId = attacker.id
                }
            }
            break
        }

        case CardType.Shock: {
            defender.disruptedSlots = Math.min(defender.maxPrepareSlots, defender.disruptedSlots + winningCardValue)
            break
        }

        case CardType.Energize:
        case CardType.ArcaneBoots:
        case CardType.Ale: {
            attacker.disruptedSlots -= winningCardValue
            break
        }

        case CardType.ThunderSprint: {
            attacker.disruptedSlots -= winningCardValue
            const attackerIsImmune = isImmuneToDamage(attacker, state.round)
            if (!attackerIsImmune && !effectResult?.damagePrevented) {
                const cursedIronCards = attacker.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalSelfDamage = 3
                if (damageReduction > 0) {
                    finalSelfDamage = Math.max(0, 3 - damageReduction)
                }
                const actualDamage = Math.max(0, finalSelfDamage - attacker.shield)
                const damageLogs = applyHealthLoss(attacker, actualDamage, attacker)
                attacker.shield = Math.max(0, attacker.shield - finalSelfDamage)
                newLog.push(...damageLogs)
            }
            break
        }

        case CardType.PsySearch: {
            newLog.push(`${attacker.name} draws ${winningCardValue} card(s).`)
            const p1DrawCount = winningCardValue
            let availableColors = Object.values(CardColor).filter(color => newColorDecks[color].length > 0)
            while (newDealingDeck.length < p1DrawCount && availableColors.length > 0) {
                const newCardsForDeck: Card[] = []
                availableColors.forEach(color => {
                    const card = newColorDecks[color].pop()
                    if (card) {
                        newCardsForDeck.push(card)
                    }
                })
                newDealingDeck = [...newDealingDeck, ...shuffle(newCardsForDeck, state.seed + '_dealing_' + newDealingDeck.length)]
                availableColors = Object.values(CardColor).filter(color => newColorDecks[color].length > 0)
            }
            const cardsToDraw = Math.min(p1DrawCount, newDealingDeck.length)
            for (let i = 0; i < cardsToDraw; i++) {
                const card = newDealingDeck.pop()
                if (card) {
                    card.ownerId = attacker.id
                    attacker.hand.push(card)
                }
            }
            break
        }

        case CardType.BodyUpgrade: {
            attacker.maxHealth += winningCardValue
            attacker.health += winningCardValue
            // Add reminder marker
            const bodyUpgradeMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(bodyUpgradeMarker)
            break
        }

        case CardType.FortunesFavor: {
            attacker.bonusDraw += winningCardValue
            // Add reminder marker
            const fortunesMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(fortunesMarker)
            break
        }

        case CardType.CursedIron: {
            // Add reminder marker - effect is passive damage reduction
            const cursedIronMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(cursedIronMarker)
            break
        }

        case CardType.EyesOfMadness: {
            attacker.maxPrepareSlots += winningCardValue
            // Add reminder marker
            const eyesMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(eyesMarker)
            break
        }

        case CardType.MadnessControl: {
            attacker.maxPrepareSlots += winningCardValue
            // Add reminder marker
            const madnessMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(madnessMarker)
            break
        }

        case CardType.BrokenSecret: {
            const colorDeckCardsInHand = defender.hand.filter(c => !c.essenceCost)
            const cardGroups = new Map<string, Card[]>()
            colorDeckCardsInHand.forEach(card => {
                const key = `${card.color}-${card.type}-${card.value}-${card.typeB || ''}-${card.valueB || ''}`
                if (!cardGroups.has(key)) {
                    cardGroups.set(key, [])
                }
                cardGroups.get(key)!.push(card)
            })
            const stolenCards: Card[] = []
            cardGroups.forEach(group => {
                const sets = Math.floor(group.length / 3)
                if (sets > 0) {
                    const cardsToSteal = group.slice(0, sets)
                    stolenCards.push(...cardsToSteal)
                }
            })
            if (stolenCards.length > 0 || colorDeckCardsInHand.length > 0) {
                const stolenCardIds = stolenCards.map(c => c.id)

                // PHASE CHANGE: Trigger Reveal instead of stealing immediately

                // We need to set the brokenSecretState on the GameState.
                // However, this function (applyCardEffect) typically returns specific effect results or relies on side-effects on the objects passed in.
                // The 'state' object is available in the scope (it is passed to applyCardEffect).
                // We will mutate the state here to set the phase and data.
                // Note: applyCardEffect is usually called within a context where state mutation is being tracked or cloned.

                // NOTE: We need to ensure we are modifying the state that is being returned/used.
                // In the current architecture, 'state' is often a clone.

                state.phase = GamePhase.BROKEN_SECRET_REVEAL;
                state.brokenSecretState = {
                    initiatorId: attacker.id,
                    targetId: defender.id,
                    stolenCardIds: stolenCardIds,
                    revealDuration: 4000,
                    startTime: Date.now()
                };

                // We do NOT move the cards yet. That happens in completeBrokenSecret.
            }
            break
        }

        case CardType.DiscerningEye: {
            const discerningEyeMarker: Card = { ...winningCard.card, type: CardType.DiscerningEye, appliedRound: state.round, ownerId: attacker.id }
            defender.effectCards.push(discerningEyeMarker as any)
            break
        }

        case CardType.Trembling: {
            incDamageUse(attacker.id, defender.id)
            const defenderIsImmune = isImmuneToDamage(defender, state.round)
            if (!defenderIsImmune && !effectResult?.damagePrevented) {
                const preparedCount = (state.preparedAtNightStart && state.preparedAtNightStart[defender.id] !== undefined)
                    ? state.preparedAtNightStart[defender.id]
                    : defender.prepared.length
                const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron)
                const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
                let finalDamageBase = preparedCount
                if (damageReduction > 0) {
                    finalDamageBase = Math.max(0, preparedCount - damageReduction)
                }
                const voodooActive = attacker.effectCards.some(c => c.type === CardType.VoodooDoll)
                if (voodooActive) finalDamageBase += 1
                const damageDealt = Math.max(0, finalDamageBase - defender.shield)
                const damageLogs = applyHealthLoss(defender, damageDealt, attacker)
                defender.shield = Math.max(0, defender.shield - finalDamageBase)
                newLog.push(...damageLogs)
            }
            break
        }

        case CardType.Frost: {
            // Frost effect (becomes Hibernation) goes ON the DEFENDER (target, who is being frozen)
            // attacker = card owner (who played Frost), defender = target
            const hibernationEffectCard: Card = { ...winningCard.card, type: CardType.Hibernation, value: 1, color: CardColor.Pink, ownerId: attacker.id, appliedRound: state.round }
            defender.effectCards.push(hibernationEffectCard)
            // The frozen player (defender) draws a card as per Hibernation rules
            const availableDrawDecks = Object.values(CardColor).filter(color => newColorDecks[color]?.length > 0)
            if (availableDrawDecks.length > 0) {
                // Neural agents go through choice phase; rule-based AI auto-selects
                if (defender.isAI && !defender.isNeuralAgent) {
                    const drawSeed = state.seed + `_frost_draw_${winningCard.card.id}`
                    const seedGen = xmur3(drawSeed)
                    const rand = mulberry32(seedGen())
                    const chosenColor = availableDrawDecks[Math.floor(rand() * availableDrawDecks.length)]
                    const cardToDraw = newColorDecks[chosenColor].pop()!
                    cardToDraw.ownerId = defender.id
                    defender.hand.push(cardToDraw)
                } else {
                    hibernationChoicePendingForPlayerId = defender.id
                }
            }
            break
        }

        case CardType.Invincible: {
            // Owner Reminder - makes the player immune to damage this night
            const invincibleMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            attacker.effectCards.push(invincibleMarker)
            newLog.push(`${attacker.name} becomes invincible this night!`)

            // Invincible returns to hand after successful activation (like Seal of Silence)
            // Exception: if played from Mage hand (mageHandCast), goes to stash discard
            if (winningCard.mageHandCast) {
                newStashDiscard.push(winningCard.card)
                newLog.push(`${attacker.name}'s Invincible spell fades to the stash discard.`)
            } else {
                // Return to hand for reuse
                attacker.hand.push(winningCard.card)
            }
            break
        }

        case CardType.DiscerningEye: {
            // Opponent Reminder - applies to the defender (opponent can see their prepared cards)
            const discerningEyeMarker: Card = { ...winningCard.card, appliedRound: state.round, ownerId: attacker.id }
            defender.effectCards.push(discerningEyeMarker)
            newLog.push(`${attacker.name} uses Discerning Eye on ${defender.name}!`)
            break
        }

        default: {
            log('applyCardEffect', `⚠️ WARNING: Unhandled card type ${winningCardType}`)
            break
        }
    }

    // Cleanup: Remove the winning card from prepared slots (it's now in effectCards or used)
    // This is especially important for effect cards that were kept in prepared until now
    const effectCardTypes = [CardType.FortunesFavor, CardType.Hibernation, CardType.Disease, CardType.CursedIron, CardType.EyesOfMadness, CardType.MadnessControl, CardType.NaturalGrip, CardType.Invincible, CardType.VoodooDoll, CardType.BodyUpgrade, CardType.SealOfSilence, CardType.Frost, CardType.DiscerningEye]
    if (effectCardTypes.includes(winningCardType)) {
        playersCopy.forEach(player => {
            player.prepared = player.prepared.filter(pc => pc.card.id !== winningCard.card.id)
        })
    }

    return {
        ...state,
        players: playersCopy,
        colorDecks: newColorDecks,
        stashDeck: newStashDeck,
        stashDiscard: newStashDiscard,
        dealingDeck: newDealingDeck,
        availableTowers: newAvailableTowers,
        drawEvents: newDrawEvents,
        log: newLog,
        eliminatedCards: newEliminatedCards,
        damageCardsUsedThisNight: damageMap,
        attemptedDamageCardsThisNight: attemptedMap,
        // Choice menu phases - priority order matters!
        // Check BOTH local variables (set this call) AND state initiatorIds (set by previous calls)
        // Hibernation (winning counter) should be handled before Chromabow (partially countered card)
        phase: (hibernationChoicePendingForPlayerId || state.hibernationInitiatorId) ? GamePhase.AWAITING_HIBERNATION_CHOICE :
            (chromabowChoicePendingForPlayerId || state.chromabowInitiatorId) ? GamePhase.AWAITING_CHROMABOW_CHOICE :
                (burglaryChoicePendingForPlayerId || state.burglaryInitiatorId) ? GamePhase.AWAITING_BURGLARY_CHOICE :
                    (purificationChoicePendingForPlayerId || state.purificationInitiatorId) ? GamePhase.AWAITING_PURIFICATION_TARGET :
                        state.phase,
        chromabowInitiatorId: chromabowChoicePendingForPlayerId || state.chromabowInitiatorId,
        hibernationInitiatorId: hibernationChoicePendingForPlayerId || state.hibernationInitiatorId,
        burglaryInitiatorId: burglaryChoicePendingForPlayerId || state.burglaryInitiatorId,
        burglaryTargetId: burglaryTargetId !== null ? burglaryTargetId : state.burglaryTargetId,
        purificationInitiatorId: purificationChoicePendingForPlayerId || state.purificationInitiatorId,
        pendingExtraDayCount: (state.pendingExtraDayCount || 0) + extraDayToSchedule
    }
}
